import { useEffect, useRef, useState } from "react";
import { App as AntApp, Button, Card, Flex, Form, Input, Modal, Space, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import type { GetRef } from "antd";
import { startLogin, verifyLogin } from "../../shared/auth.js";

const RESEND_SECONDS = 60;

/** Passwordless admin sign-in (phone → SMS OTP), via the IdP. Calls onLogin() on success. */
export function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState<{ phone: string } | null>(null);
  return (
    <Flex align="center" justify="center" style={{ minHeight: "100vh" }}>
      <Card style={{ width: 360 }}>
        <Flex vertical align="center" gap={8} style={{ marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>MyBuy Admin</Typography.Title>
          <Typography.Text type="secondary">Admin sign in</Typography.Text>
        </Flex>
        <Form form={form} layout="vertical" onFinish={async (v: { phone: string }) => {
          setBusy(true);
          try { await startLogin(v.phone); setOtp({ phone: v.phone }); }
          catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
        }}>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input autoFocus placeholder="+9725XXXXXXXX" inputMode="tel" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={busy}>Send code</Button>
        </Form>
      </Card>
      <OtpModal info={otp} onClose={() => setOtp(null)} onVerified={onLogin} />
    </Flex>
  );
}

function OtpModal({ info, onClose, onVerified }: { info: { phone: string } | null; onClose: () => void; onVerified: () => void }) {
  const { message } = AntApp.useApp();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(RESEND_SECONDS);
  const otpRef = useRef<GetRef<typeof Input.OTP>>(null);
  const open = !!info;
  useEffect(() => { if (open) { setCode(""); setLeft(RESEND_SECONDS); } }, [open]);
  useEffect(() => {
    if (!open || left <= 0) return;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, left]);
  // WebOTP (Android Chrome): auto-read the code from the SMS so the admin doesn't type it. Works only when
  // the SMS ends with the origin-bound line `@<host> #<code>` (added server-side); a harmless no-op otherwise.
  useEffect(() => {
    if (!open || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    void (navigator.credentials.get({ otp: { transport: ["sms"] }, signal: ac.signal } as unknown as CredentialRequestOptions) as Promise<unknown>)
      .then((c) => { const otp = (c as { code?: string } | null)?.code; if (otp) { setCode(otp); void verify(otp); } })
      .catch(() => {/* aborted on close / no matching SMS */});
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!info) return null;
  const verify = async (c = code) => {
    if (c.length < 6 || busy) return;
    setBusy(true);
    try { await verifyLogin(info.phone, c); onVerified(); }
    catch (e) { message.error((e as Error).message || "Invalid or expired code"); setCode(""); otpRef.current?.focus(); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try { await startLogin(info.phone); setLeft(RESEND_SECONDS); setCode(""); otpRef.current?.focus(); message.success("A new code has been sent"); }
    catch (e) { message.error((e as Error).message); }
  };
  return (
    <Modal open={open} onCancel={onClose} footer={null} maskClosable={false} width={360} centered
      title={<Space><SafetyCertificateOutlined /> Sign in</Space>}
      afterOpenChange={(o) => { if (o) otpRef.current?.focus(); }}>
      <Flex vertical gap={14} style={{ marginTop: 8 }}>
        <Typography.Text type="secondary">We sent a 6-digit code by SMS to {info.phone}. It expires in 5 minutes.</Typography.Text>
        <Flex justify="center" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void verify(); } }}>
          <Input.OTP ref={otpRef} length={6} value={code} onChange={setCode} autoFocus />
        </Flex>
        <Button type="primary" block loading={busy} disabled={code.length < 6} onClick={() => verify()}>Verify &amp; sign in</Button>
        <Flex justify="center">
          <Button type="link" disabled={left > 0} onClick={resend}>{left > 0 ? `Resend code in ${left}s` : "Resend code"}</Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
